import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setColumnWidth } from "../../../helpers/cookieUtils";
import ListingHeader from "../listingHeader/ListingHeader";
import {
  Tooltip,
  TableCellLayout,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  makeStyles,
  AvatarGroupItem,
  AvatarGroupPopover,
  AvatarGroup,
  partitionAvatarGroupItems,
  Avatar,
} from "@fluentui/react-components";
import {
  AttachementIcon,
  CheckmarkCircleIcon,
  DeleteIcon,
  DismissCircleIcon,
  EditIcon,
  FlagIcon,
  MeatBallsMenuIcon,
  PendingDeleteIcon,
  RemindIcon,
} from "../../../modules/default-svg";
import { dateOnlyFormatted, isDateGreaterThan } from "../../../utils/systemSettings";
import userService from "../../../store/services/user.service";
import moment from "moment";
import { generalActions } from "../../../store/actions";
import CustomSelect from "../select/CustomSelect";
import Spin from "../spinner/Spin";
import {
  changeRequestPriorities,
  compareArrays,
  formatSequenceNumber,
  getCreatePermission,
  ScopeStatus,
  sortColumnsByOrderlist,
} from "../../../helpers/utils";
import {
  updateTask,
  updateTaskFlag,
  createTask,
} from "../../../store/actions/scope.actions";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  isValidDate,
  localDateToISODateString,
} from "../../../utils/systemSettings";
import { Copy, Layers2, Plus, Target, TriangleAlert } from "lucide-react";
import useNavigation from "../../../utils/useNavigation";
import TableCellSafeHtml from "../../safeHtml/TableCellSafeHtml";
import { MilestoneIcon, UnActiveMilestoneIcon } from "../../../views/dashboard/icons/icons";
import HoverCard from "../../hoverCard/HoverCard";
import scopeService from "../../../store/services/scope.service";
import taskGroupService from "../../../store/services/taskGroup.service";
import CheckboxInput from "../checkbox/Checkbox";
import CreateEditTaskGroup from "../../../views/scopeOverview/CreateEditTaskGroup";
import Modal from "../modal/Modal";
import { TABLE_ACTIONS } from "./utils/tableUtils";
import { ROW_TYPE, buildScopeTreeFromBreakdown, compareByFlagAndStartDate } from "./utils/taskTree";

const getPriority = (priority) => {
  switch (priority) {
    case 0:
      return "Low";
    case 1:
      return "Moderate";
    case 2:
      return "High";
    default:
      return "";
  }
};

const getPriorityPill = (priority) => {
  switch (priority) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
      return "pill AtRisk";
    default:
      return "";
  }
};

const setRequestPriority = (type) => {
  switch (type) {
    case "Low":
      return 0;
    case "Moderate":
      return 1;
    case "High":
      return 2;
    default:
      return 0;
  }
};

const getStatusPill = (status) => {
  switch (status) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
      return "pill AtRisk";
    case 3:
      return "pill Completed";
    case 4:
      return "pill OnHold";
    default:
      return "";
  }
};

const getStatus = (status) => {
  switch (status) {
    case 1:
      return "Monitoring";
    case 2:
      return "Slipping";
    case 3:
      return "Completed";
    case 4:
      return "On Hold";
    default:
      return "In Progress";
  }
};

const GetItemSequence = (rowType, sequenceNumber) => {
    switch (rowType) {
      case ROW_TYPE.TASK_GROUP:
        return formatSequenceNumber("TaskGroup", sequenceNumber);
      default:
        return formatSequenceNumber("Task", sequenceNumber);
        
    }
  };

const hasOpenRisk = (entity) =>
  !!entity?.risks?.length && entity.risks.some((r) => r.status !== 5);

const EditInput = ({
  label,
  value,
  onChange,
  type,
  dateFormat,
  onBlur,
  table,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [resources, setResources] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const formatValue = (val) => {
    if (!val) return null;
    if (val.label && val.value) return val;
    return { label: val.name, value: val.id };
  };

  const handleDateChange = (newDate) => {
    if (isValidDate(newDate)) {
      const newValue = localDateToISODateString(newDate);
      setInputValue(newValue);
      onChange(newValue);
    }
  };

  const dropdownHandler = () => {
    if (label === "Status") {
      setDropdownOptions(() =>
        ScopeStatus.map((obj) => ({
          label: obj.name,
          value: obj.value,
        }))
      );
    } else if (label === "Priority") {
      setDropdownOptions(() =>
        changeRequestPriorities.map((obj) => ({
          label: obj.name,
          value: obj.value,
        }))
      );
    } else if (label === "Critical") {
      setDropdownOptions(() =>
        [
          { key: "Yes", value: true },
          { key: "No", value: false },
        ].map((obj) => ({
          label: obj.key,
          value: obj.value,
        }))
      );
    } else if (label === "Milestone") {
      setDropdownOptions(() =>
        [
          { key: "Yes", value: true },
          { key: "No", value: false },
        ].map((obj) => ({
          label: obj.key,
          value: obj.value,
        }))
      );
    }
  };

  const searchHandler = (value) => {
    if (value === "") {
      setResources([]);
      return;
    }
    userService
      .searchUserDataRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setResources(transformedArray);
      })
      .catch(() => {
        setResources([]);
      });
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleSelectChange = (item) => {
    if (!item) return;
    const newValue = { id: item.value, name: item.label };
    handleInputChange(newValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const creatingRowState = table.getState().creatingRow;
      if (table?.options?.onCreatingRowSave && creatingRowState) {
        const currentValues = {
          ...creatingRowState.values,
        };
        table.options.onCreatingRowSave({
          values: currentValues,
          table,
        });
      }
    }
  };

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  if (type === "text") {
    return (
      <input
        placeholder={`Enter ${label}`}
        value={inputValue}
        type="text"
        onChange={(e) => handleInputChange(e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "number") {
    return (
      <input
        placeholder={`Enter ${label}`}
        value={inputValue}
        type="number"
        min={0}
        onChange={(e) => handleInputChange(+e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "select") {
    let searchTextFunc = searchHandler;
    let options = resources;
    let selectText = "resource";
    let isCreatable = false;
    if (label === "Status") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "status";
    } else if (label === "Priority") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "status";
    } else if (label === "Critical") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "critical";
    } else if (label === "Milestone") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "milestone";
    }

    return (
      <div className="d-flex align-items-center w-full max-w-md mx-auto">
        <CustomSelect
          searchText={searchTextFunc}
          SelectText={selectText}
          CustomClass="ModalAvatarSearch CustomSelect"
          options={options}
          value={formatValue(value)}
          onChange={handleSelectChange}
          creatable={isCreatable}
          setCreated={handleSelectChange}
          handleBlur={onBlur}
          isDatatable
          selectWd="w-100"
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <DatePicker
      selected={inputValue ? moment(inputValue).toDate() : null}
      onChange={handleDateChange}
      dateFormat={dateFormat}
      className="CustomDatePickerInput"
      onBlur={onBlur}
      popperPlacement="bottom-start"
      placeholderText={dateFormat}
      onKeyDown={handleKeyDown}
      popperClassName="CustomDatePickerPopper"
      showPopperArrow={false}
    />
  );
};

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: "#342c82",
    color: "#FFFFFF",
  },
});

const TaskTable = (props) => {
  const {
    columnsList,
    rowData,
    setIsCreate,
    tableName,
    handleColumnOrdering,
    dispatch,
    perPageItems,
    handleReminder,
    handleEdit,
    handleDelete,
    scopeId,
    handleAddRow,
    handleCancelAddRow,
    handleListingView,
    handleKanbanView,
    kanbanView,
    handleAction,
    onAddTaskToGroup,
    onTaskGroupChanged,
    taskGroupId
  } = props;
  const styles = useStyles();
  const { navigateTo } = useNavigation();
  const [cols, setCols] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const setting = JSON.parse(localStorage.getItem("settings"));
  const tableContainerRef = useRef(null);
  const selectRefs = useRef({});
  const [validationErrors, setValidationErrors] = useState({});
  const [editedRows, setEditedRows] = useState({});
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerValue, setHeaderValue] = useState("");
  const [columnSizing, setColumnSizing] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [popoverStates, setPopoverStates] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [isAllRowsExpanded, setIsAllRowsExpanded] = useState(false);

  const [isTaskGroupModalOpen, setIsTaskGroupModalOpen] = useState(false);
  const [taskGroupData, setTaskGroupData] = useState(null);
  const [isEditTaskGroup, setIsEditTaskGroup] = useState(false);
  const [isDeleteTaskGroupModal, setIsDeleteTaskGroupModal] = useState(false);
  const [isDeletingTaskGroup, setIsDeletingTaskGroup] = useState(false);
  const [isDuplicateTaskGroupModal, setIsDuplicateTaskGroupModal] = useState(false);
  const [isDuplicatingTaskGroup, setIsDuplicatingTaskGroup] = useState(false);
  const [duplicateTaskGroupIncludeTasks, setDuplicateTaskGroupIncludeTasks] = useState(false);
  const [groupTasksInitialTasks, setGroupTasksInitialTasks] = useState([]);

  // Task selection, driving the "Group Tasks" toolbar action. Every task in
  // this table already belongs to the same (implicit) scope.
  const [selectedTasks, setSelectedTasks] = useState(() => new Map());
  const selectedTaskList = useMemo(() => Array.from(selectedTasks.values()), [selectedTasks]);

  const toggleTaskSelection = (item, checked) => {
    setSelectedTasks((prev) => {
      const next = new Map(prev);
      if (checked) {
        next.set(item.id, { id: item.id, name: item.name });
      } else {
        next.delete(item.id);
      }
      return next;
    });
  };

  const handlePopoverChange = (itemId, isOpen) => {
    setPopoverStates(isOpen ? { [itemId]: isOpen } : {});
  };
  const [colsList, setColumnsList] = useState([]);
  useEffect(() => {
    if (columnsList.length) {
      setColumnsList(columnsList);
    }
  }, [columnsList]);

  const fetchTasks = useCallback(() => {
    if (!scopeId) return;
    setIsLoading(true);
    const request = taskGroupId
      ? taskGroupService
        .getTaskGroupTasksRequest({ scopeId, taskGroupId })
        .then((tasks) => ({ taskGroups: [], standaloneTasks: tasks || [] }))
      : scopeService.getScopeTasksBreakdownRequest({ scopeId });
    request
      .then((res) => setBreakdown(res))
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Failed to fetch tasks",
            type: "error",
          })
        );
        setBreakdown(null);
      })
      .finally(() => setIsLoading(false));
  }, [scopeId, taskGroupId, dispatch]);

  const updateTaskInBreakdown = (taskId, updater) => {
    setBreakdown((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        standaloneTasks: (prev.standaloneTasks || []).map((t) =>
          t.id === taskId ? updater(t) : t
        ),
        taskGroups: (prev.taskGroups || []).map((g) => ({
          ...g,
          tasks: (g.tasks || []).map((t) => (t.id === taskId ? updater(t) : t)),
        })),
      };
    });
  };

  useEffect(() => {
    fetchTasks();

  }, [scopeId, taskGroupId]);

  // Standalone task create/edit/delete/duplicate happen outside this
  // component (via ScopeOverview.jsx's Redux-backed handlers) and only ever
  // update the `rowData` prop (flat task list). Task group mutations already
  // call fetchTasks() directly (see below), but standalone task mutations
  // don't — re-run fetchTasks() whenever rowData changes so this table's own
  // breakdown stays in sync with them too.
  // Skip the first invocation: it fires alongside the [scopeId, taskGroupId]
  // mount effect above (React runs every effect after the initial render
  // regardless of "changed" deps), which would otherwise double-fetch on mount.
  const isRowDataMountedRef = useRef(false);
  useEffect(() => {
    if (!isRowDataMountedRef.current) {
      isRowDataMountedRef.current = true;
      return;
    }
    if (rowData) fetchTasks();

  }, [rowData]);

  const handleReorderColumns = (results) => {
    if (results?.length) {
      const updatedColumnsDB = results.map((col) => ({
        name: col.label,
        customName: col.customName ?? "",
        order: col.order,
        isVisible: col.isVisible,
      }));

      const payload = {
        Name: tableName,
        Columns: updatedColumnsDB,
      };
      userService
        .putUserLayoutRequest(payload)
        .then(() => {
          dispatch(
            generalActions.showToast({
              title: "Column ordering updated successfully",
              type: "success",
            })
          );
        })
        .catch((err) => {
          dispatch(
            generalActions.showToast({
              title: "Error updating column ordering",
              type: "error",
            })
          );
        });
    }
  };

  const handleHeaderDoubleClick = (columnId, currentHeader) => {
    setEditingHeader(columnId);
    setHeaderValue(currentHeader);
  };

  const handleHeaderSave = (columnId) => {
    if (!headerValue.trim()) {
      dispatch(
        generalActions.showToast({
          title: "Header name cannot be empty",
          type: "error",
        })
      );
      return;
    }

    const updatedColumnsDB = colsList.map((col) => ({
      name: col.label,
      customName: col.accessorKey === columnId ? headerValue : col.customName,
      order: col.order,
      isVisible: col.isVisible,
    }));

    const payload = {
      Name: tableName,
      Columns: updatedColumnsDB,
    };

    userService
      .putUserLayoutRequest(payload)
      .then(() => { })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Error updating column header",
            type: "error",
          })
        );
      })
      .finally(() => {
        setCols((prevCols) => {
          const newCols = prevCols.map((col) =>
            col.accessorKey === columnId ? { ...col, header: headerValue } : col
          );
          return newCols;
        });
      });

    setEditingHeader(null);
    setHeaderValue("");
  };

  // Flagging a task moves it to the top of its own sibling list: inside a
  // group that's the group's task list, otherwise the standalone-task list.
  const updateAndReorderTaskFlag = (taskId, newValue) => {
    setBreakdown((prev) => {
      if (!prev) return prev;
      const groupIndex = (prev.taskGroups || []).findIndex((g) =>
        (g.tasks || []).some((t) => t.id === taskId)
      );
      if (groupIndex !== -1) {
        const newGroups = prev.taskGroups.map((g, i) => {
          if (i !== groupIndex) return g;
          const newTasks = g.tasks
            .map((t) => (t.id === taskId ? { ...t, isFlagged: newValue } : t))
            .sort(compareByFlagAndStartDate);
          return { ...g, tasks: newTasks };
        });
        return { ...prev, taskGroups: newGroups };
      }
      const newStandalone = (prev.standaloneTasks || [])
        .map((t) => (t.id === taskId ? { ...t, isFlagged: newValue } : t))
        .sort(compareByFlagAndStartDate);
      return { ...prev, standaloneTasks: newStandalone };
    });
  };

  const handleFlag = (id, status) => {
    updateAndReorderTaskFlag(id, status);
    dispatch(
      updateTaskFlag({
        id: id,
        payload: [
          {
            op: "replace",
            path: "/IsFlagged",
            value: status,
          },
        ],
      })
    );
  };

  const handleTaskGroupFlag = (item, status) => {
    setBreakdown((prev) => {
      if (!prev) return prev;
      const newGroups = (prev.taskGroups || [])
        .map((g) => (g.id === item.id ? { ...g, isFlagged: status } : g))
        .sort(compareByFlagAndStartDate);
      return { ...prev, taskGroups: newGroups };
    });
    taskGroupService.patchTaskGroupField(item.id, "IsFlagged", status).catch(() => {
      dispatch(
        generalActions.showToast({
          title: "Failed to update flag",
          type: "error",
        })
      );
      fetchTasks();
    });
  };

  const handleToggleField = (id, field, newValue) => {
    const prevBreakdown = breakdown;
    updateTaskInBreakdown(id, (t) => ({ ...t, [field]: newValue }));

    scopeService
      .patchTaskField(id, field === "isCritical" ? "IsCritical" : "IsInWsr", newValue)
      .catch(() => {
        setBreakdown(prevBreakdown);
        dispatch(
          generalActions.showToast({
            title: `Failed to update ${field === "isCritical" ? "Critical" : "In WSR"} status`,
            type: "error",
          })
        );
      });
  };

  const handleToggleMilestone = (id, newValue) => {
    const prevBreakdown = breakdown;
    updateTaskInBreakdown(id, (t) => ({ ...t, isMilestone: newValue }));

    scopeService
      .updateTaskMilestoneStatus(id, newValue)
      .catch(() => {
        setBreakdown(prevBreakdown);
        dispatch(
          generalActions.showToast({
            title: "Failed to update Milestone status",
            type: "error",
          })
        );
      });
  };

  const closeTaskGroupModal = () => {
    setIsTaskGroupModalOpen(false);
    setTaskGroupData(null);
    setIsEditTaskGroup(false);
    setGroupTasksInitialTasks([]);
  };

  const handleTaskGroupEdit = (item) => {
    setTaskGroupData(item);
    setIsEditTaskGroup(true);
    setIsTaskGroupModalOpen(true);
  };

  const handleTaskGroupCreate = () => {
    setTaskGroupData(null);
    setIsEditTaskGroup(false);
    setGroupTasksInitialTasks([]);
    setIsTaskGroupModalOpen(true);
  };

  const handleGroupSelectedTasks = () => {
    if (!selectedTaskList.length) return;
    setTaskGroupData(null);
    setIsEditTaskGroup(false);
    setGroupTasksInitialTasks(selectedTaskList);
    setIsTaskGroupModalOpen(true);
  };

  const handleTaskGroupDeleteConfirm = (group) => {
    setIsDeletingTaskGroup(true);
    taskGroupService
      .deleteTaskGroupRequest({ id: group.id })
      .then(() => {
        dispatch(
          generalActions.showToast({
            title: "Task group deleted successfully.",
            type: "success",
          })
        );
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsDeletingTaskGroup(false);
        setIsDeleteTaskGroupModal(false);
        fetchTasks();
        onTaskGroupChanged?.();
        setTaskGroupData(null);
      });
  };

  const handleTaskGroupDuplicate = (e, item, isIncludeTasks = false) => {
    e.stopPropagation();
    e.preventDefault();
    handlePopoverChange(item.id, !popoverStates[item.id]);
    setTaskGroupData(item);
    setDuplicateTaskGroupIncludeTasks(isIncludeTasks);
    setIsDuplicateTaskGroupModal(true);
  };

  const handleTaskGroupDuplicateConfirm = () => {
    if (!taskGroupData) return;
    setIsDuplicatingTaskGroup(true);
    taskGroupService
      .duplicateTaskGroupRequest({
        id: taskGroupData.id,
        payload: { IsIncludeTasks: duplicateTaskGroupIncludeTasks },
      })
      .then(() => {
        dispatch(
          generalActions.showToast({
            title: "Task group duplicated successfully.",
            type: "success",
          })
        );
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsDuplicatingTaskGroup(false);
        setIsDuplicateTaskGroupModal(false);
        fetchTasks();
        onTaskGroupChanged?.();
        setTaskGroupData(null);
      });
  };

  const getTaskTotalHours = (allocations) => {
    if (allocations?.length > 0) {
      const effort = allocations.reduce((sum, obj) => {
        return sum + parseInt(obj.totalHours || 0, 10);
      }, 0);
      return effort;
    }
    return 0;
  };

  const getResources = (rowType, item) => {

    let resources = [];


    switch (rowType) {
      case ROW_TYPE.TASK:
        resources = item.allocations;
        break;
      case ROW_TYPE.TASK_GROUP:
        resources = [
          ...new Map(
            item.subRows
              .flatMap(row => row.allocations ?? [])
              .map(allocation => [allocation.resource.id, allocation])
          ).values(),

        ];
        break;
    }

    if (!resources || resources?.length === 0)
      return "";

    const { inlineItems, overflowItems } = partitionAvatarGroupItems({
      items: resources,
    });
    return (
      <AvatarGroup layout="stack" className="listingAvatarGroup" size={20}>
        {inlineItems.map((item) => (
          <HoverCard
            HoverCardTrigger={
              <Avatar
                color="colorful"
                size={20}
                name={item.resource.name}
                image={{ src: item.resource.image }}
                key={item.resource.name}
              />
            }

            AvatarId={item.resource?.id}
            AvatarName={item.resource?.name}
            AvatarImage={item.resource?.image}
            AvatarDesignation={item.resource?.title || ""}
            AvatarEmail={item.resource?.email}
            AvatarPhone={item.resource?.phoneNumber}
            AvatarIsExternal={item.resource?.isExternal}
          />
        ))}
        {overflowItems && (
          <AvatarGroupPopover>
            {overflowItems.map((item) => (
              <div className="d-flex inline-items gap-2">
                <HoverCard
                  HoverCardTrigger={
                    <Avatar
                      color="colorful"
                      size={20}
                      name={item.resource.name}
                      image={{ src: item.resource.image }}
                      key={item.resource.name}
                    />
                  }
                  AvatarId={item.resource?.id}
                  AvatarName={item.resource?.name}
                  AvatarImage={item.resource?.image}
                  AvatarDesignation={item.resource?.title || ""}
                  AvatarEmail={item.resource?.email}
                  AvatarPhone={item.resource?.phoneNumber}
                  AvatarIsExternal={item.resource?.isExternal}
                />
                <p className="font-figtree text-[13px]">{item.resource.name}</p>
              </div>
            ))}
          </AvatarGroupPopover>
        )}
      </AvatarGroup>
    );
  };

  const handleSave = (rowId, data) => {
    setEditedRows({});
    const payload = {
      Name: data.name,
      RefId: data.refId ?? null,
      startDate: data.startDate ?? null,
      endDate: data.endDate ?? null,
      completionDate: data.completionDate ?? null,
      Description: data.description ? `<p>${data.description}</p>` : "",
      Phase: data.phase ?? null,
      Priority: data.priority ?? null,
      ScopeId: scopeId,
      AssignedResources: (() => {
        const { assignments, resources } = data || {};
        const hasAssignments = assignments?.length > 0;
        const hasResources = resources?.id;

        if (!hasAssignments && hasResources) {
          return [
            { ResourceId: resources.id, EstimatedEffort: 0, Allocations: [] },
          ];
        }

        if (
          hasAssignments &&
          hasResources &&
          !assignments.some((a) => a.ResourceId === resources.id)
        ) {
          return [
            ...assignments.map((item) => ({
              ResourceId: item.resource.id,
              EstimatedEffort: item.estimatedEffort,
              Allocations: [],
            })),
            { ResourceId: resources.id, EstimatedEffort: 0, Allocations: [] },
          ];
        }

        return (
          assignments?.map((item) => ({
            ResourceId: item.resource.id,
            EstimatedEffort: item.estimatedEffort,
            Allocations: [],
          })) || []
        );
      })(),
      progress: data.progress ?? null,
      status: data.status ?? null,
      isMilestone: data.isMilestone ? data.isMilestone.id : false,
      isCritical: data.isCritical ? data.isCritical.id : false,
    };
    dispatch(updateTask({ id: scopeId, tId: rowId, payload }));
  };

  const handleCreate = (payload) => {
    dispatch(createTask({ id: scopeId, payload, perPageItems }));
  };

  const getSortingFunction = (col) => {
    switch (col.label) {
      case "Task":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.name || "").toLowerCase();
          const b = (rowB.original.name || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Sequence #":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.sequenceNumber || 0;
          const b = rowB.original.sequenceNumber || 0;
          return a - b;
        };
      case "%":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.progress) || 0;
          const b = parseFloat(rowB.original.progress) || 0;
          return a - b;
        };
      case "Description":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.description || "")
            .toLowerCase()
            .replace(/<[^>]*>/g, ""); // Strip HTML tags
          const b = (rowB.original.description || "")
            .toLowerCase()
            .replace(/<[^>]*>/g, "");
          return a.localeCompare(b);
        };
      case "Phase":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.phase || "").toLowerCase();
          const b = (rowB.original.phase || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Status":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.status || 0;
          const b = rowB.original.status || 0;
          return a - b;
        };
      case "Priority":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.priority || 0;
          const b = rowB.original.priority || 0;
          return a - b;
        };
      case "Critical":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.isCritical ? 1 : 0;
          const b = rowB.original.isCritical ? 1 : 0;
          return b - a; // Critical items first
        };
      case "Milestone":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.isMilestone ? 1 : 0;
          const b = rowB.original.isMilestone ? 1 : 0;
          return b - a; // Milestone items first
        };
      case "In WSR":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.isInWsr ? 1 : 0;
          const b = rowB.original.isInWsr ? 1 : 0;
          return b - a; // isInWsr items first
        };
      case "Start Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.startDate || 0);
          const b = new Date(rowB.original.startDate || 0);
          return a.getTime() - b.getTime();
        };
      case "End Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.endDate || 0);
          const b = new Date(rowB.original.endDate || 0);
          return a.getTime() - b.getTime();
        };
      case "Total Hours":
        return (rowA, rowB, columnId) => {
          const getEstimatedHoursValue = (allocations) => {
            if (!allocations || allocations.length === 0) return 0;
            return allocations.reduce(
              (total, allocation) =>
                total + (parseFloat(allocation.estimatedEffort) || 0),
              0
            );
          };

          const a = getEstimatedHoursValue(rowA.original.allocations);
          const b = getEstimatedHoursValue(rowB.original.allocations);
          return a - b;
        };
      case "Resources":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.allocations?.length || 0;
          const b = rowB.original.allocations?.length || 0;
          return a - b;
        };
      default:
        return "alphanumeric";
    }
  };

  const getSortDescFirst = (col) => {
    const descFirstColumns = [
      "%",
      "Critical",
      "Milestone",
      "In WSR",
      "Start Date",
      "End Date",
      "Total Hours",
    ];
    return descFirstColumns.includes(col.label);
  };

  const handleTaskDuplicate = (e, item, isIncludeTasks = false) => {
    e.stopPropagation();
    e.preventDefault();
    handlePopoverChange(
      item.id,
      !popoverStates[item.id]
    );

    if (handleAction) {
      handleAction({
        actionType: TABLE_ACTIONS.DUPLICATE,
        payload: {
          id: item.id,
          name: item.name,
        }
      });
    }
  };

  useEffect(() => {
    if (!colsList?.length) return;
    const columnsOrder = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => item.accessorKey);
    setColumnOrder(["mrt-row-expand", ...columnsOrder]);
    const columns = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col, index) => {
        return {
          Label: col.label,
          CustomName: col.customName,
          Order: col.order,
          Visibility: col.isVisible,
          accessorKey: col.accessorKey,
          header: col.header,
          enableHiding: true,
          enableColumnActions: false,
          grow: true,
          enableResizing: true,
          size: col.size,
          editVariant: col.columnType,
          enableGlobalFilter: true,
          enableEditing: (row) => {
            const rowType = row.original.rowType || ROW_TYPE.TASK;
            if (rowType !== ROW_TYPE.TASK) return false;
            return col.accessorKey !== "estimatedHours";
          },
          enableSorting: editingHeader === col.accessorKey ? false : true,
          sortingFn: getSortingFunction(col),
          sortDescFirst: getSortDescFirst(col),
          muiEditTextFieldProps: ({ cell, row }) => ({
            sx: {
              fontFamily: "Figtree-Regular",
              fontSize: "14px",
              padding: "0.5rem",
              maxHeight: "28px",
            },
            required: col.required || false,
            error: !!validationErrors?.[`${row.id}_${cell.column.id}`],
            helperText: validationErrors?.[`${row.id}_${cell.column.id}`],
            onFocus: () =>
              setValidationErrors({
                ...validationErrors,
                [cell.column.id]: undefined,
              }),
            inputRef: (el) => {
              if (col.columnType === "select") {
                selectRefs.current[`${row.id}_${cell.column.id}`] = el;
              }
            },
            onChange: (event) => {
              const newValue = event.target.value;
              setEditedRows((prev) => ({
                ...prev,
                [row.id]: {
                  ...prev[row.id],
                  [cell.column.id]: newValue,
                },
              }));
            },
            onBlur: (event) => {
              const value = event.target.value;
              const validationError =
                col.required && !value ? "Required" : undefined;
              setValidationErrors((prev) => ({
                ...prev,
                [`${row.id}_${cell.column.id}`]: validationError,
              }));
            },
          }),
          Cell: ({ cell, row }) => {
            const item = row.original;
            const [isHovered, setIsHovered] = useState(false);
            const rowType = item.rowType || ROW_TYPE.TASK;
            switch (col.label) {
              case "Task": {
                if (rowType === ROW_TYPE.TASK_GROUP) {
                  return (
                    <div
                      className="d-flex justify-content-between align-items-center inputWidth"
                    >
                      <div className="d-flex align-items-center inputWidth task-cell-content">
                        <Tooltip
                          positioning="before"
                          content={{
                            children: item.isFlagged ? "Unflag" : "Flag",
                            className: styles.tooltip,
                          }}
                          relationship="label"
                          withArrow
                        >
                          <span
                            className={`flag ${item.isFlagged ? "flagActive" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleTaskGroupFlag(item, !item.isFlagged);
                            }}
                          >
                            <FlagIcon />
                          </span>
                        </Tooltip>
                        <span className="mr-2 min-w-[16px]">
                          {(item.subRows || []).some(hasOpenRisk) && (
                            <Tooltip content="Includes tasks with risks that may affect delivery" relationship="label">
                              <TriangleAlert size={16} color="#d1424f" />
                            </Tooltip>
                          )}
                        </span>
                        <span
                          className="d-flex align-items-center w-full text-truncate cursor-pointer"
                          onClick={() => navigateTo("TaskGroups", item.id, item.name)}
                        >
                          {item.name}
                        </span>
                        <div className="meatballs-trigger">
                          <Popover
                            open={popoverStates[item.id]}
                            onOpenChange={(e, data) => handlePopoverChange(item.id, data.open || false)}
                            positioning="below-start"
                          >
                            <PopoverTrigger disableButtonEnhancement>
                              <TableCellLayout
                                media={<MeatBallsMenuIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handlePopoverChange(item.id, !popoverStates[item.id]);
                                }}
                              />
                            </PopoverTrigger>
                            <PopoverSurface tabIndex={-1} className="listingPopoverWrapper">
                              <div className="listingPopoverContent">
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handlePopoverChange(item.id, !popoverStates[item.id]);
                                    handleTaskGroupEdit(item);
                                  }}
                                >
                                  <EditIcon />
                                  <p className="text-sm-400 ms-12">Edit</p>
                                </div>
                                {onAddTaskToGroup && getCreatePermission("Task") && (
                                  <div
                                    className="popItem"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handlePopoverChange(item.id, !popoverStates[item.id]);
                                      onAddTaskToGroup(item);
                                    }}
                                  >
                                    <Plus size={16} />
                                    <p className="text-sm-400 ms-12">Add Task</p>
                                  </div>
                                )}
                                {getCreatePermission("Task") && (
                                  item.tasksCount === 0 ? (
                                    <div
                                      className="popItem"
                                      onClick={(e) => { handleTaskGroupDuplicate(e, item) }}
                                    >
                                      <Copy size={16} />
                                      <p className="text-sm-400 ms-12">Duplicate</p>
                                    </div>
                                  ) : (
                                    <Popover positioning="after-top" trapFocus>
                                      <PopoverTrigger disableButtonEnhancement>
                                        <div className="popItem">
                                          <Copy size={16} />
                                          <p className="text-sm-400 ms-12">Duplicate</p>
                                        </div>
                                      </PopoverTrigger>
                                      <PopoverSurface className="listingPopoverWrapper">
                                        <div className="listingPopoverContent">
                                          <div
                                            className="popItem"
                                            onClick={(e) => { handleTaskGroupDuplicate(e, item) }}
                                          >
                                            <Target size={16} />
                                            <p className="text-sm-400 ms-12">{"Group"}</p>
                                          </div>
                                          <div
                                            className="popItem"
                                            onClick={(e) => { handleTaskGroupDuplicate(e, item, true) }}
                                          >
                                            <Layers2 size={16} />
                                            <p className="text-sm-400 ms-12">{"Group & tasks"}</p>
                                          </div>
                                        </div>
                                      </PopoverSurface>
                                    </Popover>
                                  )
                                )}
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handlePopoverChange(item.id, !popoverStates[item.id]);
                                    setTaskGroupData(item);
                                    setIsDeleteTaskGroupModal(true);
                                  }}
                                >
                                  <DeleteIcon />
                                  <p className="text-sm-400 ms-12">Delete</p>
                                </div>
                              </div>
                            </PopoverSurface>
                          </Popover>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    className="d-flex justify-content-between align-items-center inputWidth"
                    style={item.taskGroup == null ? { position: 'relative', left: '-39px' } : { paddingLeft: (row.depth || 0) * 15 }}
                  >
                    <div
                      className="d-flex align-items-center inputWidth task-cell-content"
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                    >
                      {!taskGroupId && item.taskGroup == null && (
                        <span
                          className="mr-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CheckboxInput
                            checked={selectedTasks.has(item.id)}
                            setChecked={(checked) => toggleTaskSelection(item, checked)}
                          />
                        </span>
                      )}
                      <Tooltip
                        positioning="before"
                        content={{
                          children: item.isFlagged ? "Unflag" : "Flag",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span
                          className={`flag ${item.isFlagged ? "flagActive" : ""
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleFlag(item.id, !item.isFlagged);
                          }}
                        >
                          <FlagIcon />
                        </span>
                      </Tooltip>
                      <span className="mr-2 min-w-[16px]">
                        {item.risks && item.risks.length > 0 && item.risks.some(r => r.status !== 5) && (
                          <Tooltip content="Task includes risks that may affect delivery" relationship="label">
                            <TriangleAlert size={16} color="#d1424f" />
                          </Tooltip>
                        )}
                      </span>
                      <span
                        className="d-flex w-full text-truncate cursor-pointer"
                        onClick={() => handleEdit(item)}
                      >
                        {item.attachementsCount > 0 && (
                          <div className="mr-1">
                            <AttachementIcon count={item.attachementsCount} />
                          </div>
                        )}
                        {item.isPendingDelete && (
                          <div className="mr-1">
                            <PendingDeleteIcon />
                          </div>
                        )}
                        {item.name}
                      </span>
                      <div
                        className="meatballs-trigger"
                        style={{
                          opacity: isHovered ? 1 : 0,
                          visibility: isHovered ? "visible" : "hidden",
                          transition: "opacity 0.2s ease-in-out",
                        }}
                      >
                        <Popover
                          open={popoverStates[item.id]}
                          onOpenChange={(e, data) => handlePopoverChange(item.id, data.open || false)}
                          positioning="below-start"
                        >
                          <PopoverTrigger disableButtonEnhancement>
                            <TableCellLayout
                              media={<MeatBallsMenuIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                              }}
                            />
                          </PopoverTrigger>
                          <PopoverSurface
                            tabIndex={-1}
                            className="listingPopoverWrapper"
                          >
                            <div className="listingPopoverContent">
                              <div
                                className="popItem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handlePopoverChange(
                                    item.id,
                                    !popoverStates[item.id]
                                  );
                                  handleEdit(item);
                                }}
                              >
                                <EditIcon />
                                <p className="text-sm-400 ms-12">Edit</p>
                              </div>
                              {getCreatePermission("Task") && (
                                <div
                                  className="popItem"
                                  onClick={(e) => { handleTaskDuplicate(e, item) }}
                                >
                                  <Copy size={16} />
                                  <p className="text-sm-400 ms-12">Duplicate</p>
                                </div>
                              )}
                              <div
                                className="popItem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleReminder(item);
                                  handlePopoverChange(
                                    item.id,
                                    !popoverStates[item.id]
                                  );
                                }}
                              >
                                <RemindIcon />
                                <p className="text-sm-400 ms-12">Remind me</p>
                              </div>
                              <div
                                className="popItem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handlePopoverChange(
                                    item.id,
                                    !popoverStates[item.id]
                                  );
                                  handleDelete(item);
                                }}
                              >
                                <DeleteIcon />
                                <p className="text-sm-400 ms-12">Delete</p>
                              </div>
                            </div>
                          </PopoverSurface>
                        </Popover>
                      </div>
                    </div>
                  </div>
                );
              }
              case "Sequence #":
                return GetItemSequence(rowType, item.sequenceNumber);
              case "%":
                return item.progress ? `${item.progress}%` : "";
              case "Description":
                return <TableCellSafeHtml html={item.description} />;
              case "Phase":
                return item.phase;
              case "Status":
                return (
                  <div className="flex w-100 justify-center items-center">
                    <p
                      className={`w-100 ${getStatusPill(item.status)}`}
                    >
                      {getStatus(item.status)}
                    </p>
                  </div>
                );
              case "Priority":
                return (
                  <div className="flex w-100 justify-center items-center">
                    <p className={`${getPriorityPill(item.priority)} w-100`}>
                      {getPriority(item.priority)}
                    </p>
                  </div>
                );
              case "Critical":
                if (rowType === ROW_TYPE.TASK_GROUP) return null;
                return (
                  <div
                    className="flex w-100 justify-center items-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleField(item.id, "isCritical", !item.isCritical);
                    }}
                  >
                    {item.isCritical ? (
                      <CheckmarkCircleIcon />
                    ) : (
                      <DismissCircleIcon />
                    )}
                  </div>
                );
              case "Milestone":
                if (rowType === ROW_TYPE.TASK_GROUP) return null;
                return (
                  <div
                    className="flex w-100 justify-center items-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleMilestone(item.id, !item.isMilestone);
                    }}
                  >
                    {item.isMilestone ? (
                      <MilestoneIcon size={20} />
                    ) : (
                      <Tooltip
                        positioning="before"
                        content={{
                          children: "Mark as Milestone",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span className="milestone-inactive">
                          <UnActiveMilestoneIcon size={20} />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                );
              case "In WSR":
                if (rowType === ROW_TYPE.TASK_GROUP) return null;
                return (
                  <div
                    className="flex w-100 justify-center items-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleField(item.id, "isInWsr", !item.isInWsr);
                    }}
                  >
                    {item.isInWsr ? (
                      <CheckmarkCircleIcon />
                    ) : (
                      <DismissCircleIcon />
                    )}
                  </div>
                );
              case "Start Date":
                return item.startDate
                  ? dateOnlyFormatted(setting, item.startDate)
                  : "";
              case "End Date":
                return item.endDate
                  ? dateOnlyFormatted(setting, item.endDate)
                  : "";
              case "Total Hours":
                if (rowType === ROW_TYPE.TASK_GROUP) return null;
                return item?.allocations
                  ? `${getTaskTotalHours(item?.allocations)}h`
                  : "";
              case "Resources":
                return (
                  <div className="w-full flex justify-center">
                    {getResources(rowType, item)}
                  </div>
                );
              default:
                return cell.getValue();
            }
          },
          Edit: ({ cell, row, table }) => {
            const value =
              editedRows[row.id]?.[cell.column.id] ??
              cell.getValue() ??
              row.original[cell.column.id] ??
              (col.columnType === "select" ? null : "");
            return (
              <EditInput
                label={col.label}
                value={value}
                onChange={(newValue) => {
                  if (row.id.startsWith("mrt-row-create")) {
                    table.setCreatingRow((prev) => ({
                      ...prev,
                      values: {
                        ...prev.values,
                        [cell.column.id]: newValue,
                      },
                    }));
                  } else {
                    setEditedRows((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        [cell.column.id]: newValue,
                      },
                    }));
                  }
                }}
                onBlur={() => {
                  if (!row.id.startsWith("mrt-row-create")) {
                    const newValue = editedRows[row.id]?.[cell.column.id];
                    table.options.onEditingCellSave?.({
                      cell,
                      value: newValue,
                      row,
                      table,
                    });
                    table.setEditingCell(null);
                  }
                }}
                dateFormat={
                  col.columnType === "date"
                    ? setting.regionalSettings.dateFormat
                    : null
                }
                type={col.columnType}
                table={table}
              />
            );
          },
          Header: ({ column }) => {
            return editingHeader === column.id ? (
              <input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                onBlur={() => handleHeaderSave(column.id)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleHeaderSave(column.id);
                  }
                }}
                autoFocus
                className="header-edit-input"
                style={{
                  minWidth: column.getSize() - 60,
                }}
              />
            ) : (
              <span>{column.columnDef.header}</span>
            );
          },
        };
      });
    setCols(columns);
  }, [colsList, editingHeader, headerValue, selectedTasks]);

  const allTreeRows = useMemo(
    () => buildScopeTreeFromBreakdown(breakdown),
    [breakdown]
  );

  const treeRows = useMemo(() => {
    if (!searchTerm) return allTreeRows;
    const term = searchTerm.toLowerCase();
    const matchesName = (name) => (name || "").toLowerCase().includes(term);
    return allTreeRows.reduce((acc, row) => {
      if (row.rowType === ROW_TYPE.TASK_GROUP) {
        if (matchesName(row.name)) {
          acc.push(row);
          return acc;
        }
        const matchingSubRows = (row.subRows || []).filter((t) => matchesName(t.name));
        if (matchingSubRows.length > 0) {
          acc.push({ ...row, subRows: matchingSubRows });
        }
        return acc;
      }
      if (matchesName(row.name)) acc.push(row);
      return acc;
    }, []);
  }, [allTreeRows, searchTerm]);

  const table = useMaterialReactTable({
    columns: cols,
    data: treeRows,
    getRowId: (row) => row.id,
    getSubRows: (row) => row.subRows,
    enableExpanding: (row) =>
      !taskGroupId && row.original.rowType === ROW_TYPE.TASK_GROUP && (row.original.subRows?.length || 0) > 0,
    getRowCanExpand: (row) =>
      !taskGroupId && row.original.rowType === ROW_TYPE.TASK_GROUP && (row.original.subRows?.length || 0) > 0,
    enableHiding: true,
    enableSorting: true,
    manualSorting: false,
    visibleInShowHideMenu: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "cell",
    globalFilterFn: "contains",
    enableEditing: false,
    enableFilters: false,
    enableCreatingRow: true,
    createDisplayMode: "row",
    positionCreatingRow: "bottom",
    initialState: {
      density: "compact",
      showGlobalFilter: false,
      showColumnFilters: false,
      enableHiding: false,
      columnOrder: cols.map((col) => col.order),
    },
    state: {
      sorting,
      isLoading: treeRows.length === 0 && isLoading,
      columnSizing,
      columnOrder,
      showLoadingOverlay: false,
      expanded: expandedRows,
    },
    onSortingChange: setSorting,
    muiExpandButtonProps: ({ row }) => ({
      onClick: (e) => {
        e.stopPropagation();
        const isCurrentlyExpanded = expandedRows[row.id];
        setExpandedRows((prev) => ({ ...prev, [row.id]: !isCurrentlyExpanded }));
      },
      sx: {
        visibility: row.original.rowType === ROW_TYPE.TASK_GROUP ? "visible" : "hidden",
      },
    }),
    muiExpandAllButtonProps: () => ({
      onClick: (e) => {
        e.stopPropagation();
        if (isAllRowsExpanded) {
          setExpandedRows({});
        } else {
          const next = {};
          treeRows.forEach((r) => {
            if (r.rowType === ROW_TYPE.TASK_GROUP) next[r.id] = true;
          });
          setExpandedRows(next);
        }
        setIsAllRowsExpanded(!isAllRowsExpanded);
      },
    }),
    onCreatingRowCancel: () => {
      setValidationErrors({});
      handleCancelAddRow();
    },
    onCreatingRowSave: ({ values, table }) => {
      const errors = {};
      if (!values?.name) errors["name"] = `The Name field is required.`;
      if (
        values?.startDate &&
        values?.endDate &&
        isDateGreaterThan(values?.startDate, values?.endDate)
      ) {
        errors[
          "endDate"
        ] = `End date must be equal to or greater than start date.`;
      }
      if (Object.keys(errors).length) {
        dispatch(
          generalActions.showToast({
            title: errors.name || errors.endDate,
            type: "error",
          })
        );
        return;
      }

      const payload = {
        ...(values?.name && { Name: values?.name }),
        ...(values?.refId && { RefId: values?.refId }),
        ...(values?.startDate && { startDate: values?.startDate }),
        ...(values?.endDate && { endDate: values?.endDate }),
        ...(values?.completionDate && {
          completionDate: values?.completionDate,
        }),
        ...(values?.description && { Description: values?.description }),
        ...(values?.phase && { Phase: values?.phase }),
        ...(values?.status && { status: values?.status.id }),
        ...(values?.priority && {
          Priority: setRequestPriority(values?.priority?.id),
        }),
        ...(values?.isMilestone && { isMilestone: values?.isMilestone }),
        ...(values?.isCritical && { isCritical: values?.isCritical }),
        scopeId: scopeId,
        ...(taskGroupId && { TaskGroupId: taskGroupId }),
        AssignedResources: values?.resources ?? [],
        progress: values?.progress,
      };
      handleCreate(payload);
      table.setCreatingRow(null);
    },
    onEditingRowCancel: () => {
      setValidationErrors({});
      setEditedRows({});
    },
    positionGlobalFilter: "left",
    onEditingCellSave: ({ cell, row }) => {
      const editedRow = editedRows[row.id];
      const newValue =
        editedRow?.[cell.column.id] !== undefined
          ? editedRow[cell.column.id]
          : cell.getValue();

      if (newValue === cell.getValue()) return; // Skip if no real change

      // Remove the specific cell edit
      setEditedRows((prev) => {
        const updated = { ...prev };
        if (updated[row.id]) {
          delete updated[row.id][cell.column.id];
          if (Object.keys(updated[row.id]).length === 0) {
            delete updated[row.id];
          }
        }
        return updated;
      });

      const prePayload =
        cell.column.id === "status"
          ? {
            ...row.original,
            status: ScopeStatus.find((item) => item.name === newValue.name)
              ?.value,
          }
          : cell.column.id === "priority"
            ? {
              ...row.original,
              priority: setRequestPriority(newValue.name),
            }
            : {
              ...row.original,
              [cell.column.id]: newValue,
            };
      handleSave(row.original.id, prePayload);
    },
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const newSizing =
          typeof updater === "function" ? updater(prev) : updater;
        Object.entries(newSizing).forEach(([columnId, width]) => {
          setColumnWidth(tableName, columnId, width);
        });
        return newSizing;
      });
    },
    onColumnOrderChange: (updater) => {
      const newOrder = typeof updater === "function" ? updater() : updater;
      const neworderList = newOrder
        .filter((item) => item !== "mrt-row-spacer")
        .filter(Boolean);
      const areEqual = compareArrays(neworderList, columnOrder);
      if (areEqual) {
        return newOrder;
      }
      const sortedColumns = sortColumnsByOrderlist(colsList, neworderList);
      handleReorderColumns(sortedColumns);
      setColumnOrder(neworderList);
      return neworderList;
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontFamily: "Figtree-Medium",
        fontSize: "14px",
        padding: "1rem 0.5rem 1rem 1rem",
        backgroundColor: "#f8f7fc",
        color:"#342c82",
        height: "40px",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ece8fd",
          "& .MuiDivider-root": {
            opacity: 1,
          },
          "& .MuiButtonBase-root": {
            opacity: 1,
          },
          fontWeight: 700,
        },
        position: "relative",
        "& .Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
        },
        "& .MuiTableSortLabel-root": {
          marginLeft: "8px",
          paddingTop: "3px",
          opacity: 0,
          "&:hover": {
            opacity: 1,
          },
        },
        "& .MuiDivider-root": {
          cursor: "col-resize",
          backgroundColor: "#342C82",
          opacity: 0,
        },
        "& .MuiButtonBase-root": {
          cursor: "grab",
          opacity: 0,
          "& .MuiSvgIcon-root": {
            path:{
              fill: "#342C82"
            }
          }
        },
        ...(column.id === "mrt-row-expand" && {
          maxWidth: "45px",
          width: "45px",
          minWidth: "45px",
          padding: "1rem 0.5rem",
        }),
      },
      onDoubleClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleHeaderDoubleClick(column.id, column.columnDef.header);
      },
    }),
    muiTopToolbarProps: {
      showColumnFilters: false,
      showGlobalFilter: true,
      enableHiding: false,
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& .MuiBox-root": {
          padding: 0,
          display: "flex",
          alignItems: "center",
        },
      },
    },
    renderTopToolbar: () => (
      <ListingHeader
        isHeader={false}
        type="Tasks"
        parentType={taskGroupId ? "TaskGroup" : "Scope"}
        btnLable="Add Task"
        searchedData={() => { }}
        setIsCreate={() => setIsCreate(true)}
        rightPane={true}
        scopeId={scopeId}
        taskGroupId={taskGroupId}
        isCreatable={true}
        isSearchDisable={cols.length}
        handleCreateFunc={() => {
          handleAddRow();
          table.setCreatingRow(true);
        }}
        handleListingView={handleListingView}
        handleKanbanView={handleKanbanView}
        kanbanView={kanbanView}
        triggerSearch={(term) => setSearchTerm(term || "")}
        secondaryAction={
          !taskGroupId && getCreatePermission("Task")
            ? { label: "Add Task Group", onClick: handleTaskGroupCreate }
            : undefined
        }
        selectionAction={
          !taskGroupId && selectedTaskList.length > 0
            ? {
              label: `Group ${selectedTaskList.length} Task${selectedTaskList.length > 1 ? "s" : ""}`,
              onClick: handleGroupSelectedTasks,
            }
            : undefined
        }
      />
    ),
    muiTableBodyProps: {
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",

        marginBottom: "66px",
        "& .MuiTableBody-Cell": {
          "&:hover": {
            backgroundColor: "#ece8fd15",
          },
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: "32px",
        position: "relative",
        "&:hover": {
          backgroundColor: "#ece8fd15",
        },
        ...(expandedRows[row.id] && {
          backgroundColor: "#ece8fd15",
          "& td": {
            fontWeight: "bold",
          },
        }),
      },
      className: "hoverable-row",
    }),
    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight || 115
          }px)`,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#342c82",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: "#342c82",
          },
        },
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiSkeletonProps: {
      height: 34,
      sx: {
        background:
          "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        "@keyframes shimmer": {
          "0%": {
            backgroundPosition: "200% 0",
          },
          "100%": {
            backgroundPosition: "-200% 0",
          },
        },
      },
    },
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",
        padding: "0.5rem 1rem",
        position: "relative",
        overflow: "visible",
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          fontFamily: "Figtree-Medium",
          fontWeight: "bold",
        },
        ...(column.id === "mrt-row-expand" && {
          maxWidth: "45px",
          width: "45px",
          minWidth: "45px",
          padding: "0px 0.5rem",
        }),
      },
    }),
    renderEmptyRowsFallback: () => {
      return isLoading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : treeRows.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              className="mb-4"
            >
              <path
                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                fill="#342C82"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No data currently available!
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              New entries will appear here once created.
            </p>
          </div>
        </div>
      ) : null;
    },
  });

  return (
    <>
      <MaterialReactTable table={table} />

      {isTaskGroupModalOpen && (
        <CreateEditTaskGroup
          taskGroup={taskGroupData}
          isEdit={isEditTaskGroup}
          scopeId={scopeId}
          requireScopePicker={false}
          initialTasks={!isEditTaskGroup ? groupTasksInitialTasks : []}
          isOpen={isTaskGroupModalOpen}
          onClose={closeTaskGroupModal}
          onSaved={() => {
            fetchTasks();
            onTaskGroupChanged?.();
            setSelectedTasks(new Map());
            closeTaskGroupModal();
          }}
        />
      )}

      {isDeleteTaskGroupModal && (
        <Modal
          title="Delete Task Group"
          body={
            <p className="text-sm-400 subHeading-color">
              {`Deleting "${taskGroupData?.name || "this task group"}" will also delete its grouped tasks. Are you sure you want to continue?`}
            </p>
          }
          handleCancel={() => {
            setIsDeleteTaskGroupModal(false);
            setTaskGroupData(null);
          }}
          handleSave={() => handleTaskGroupDeleteConfirm(taskGroupData)}
          saveBtnLabel="Delete"
          cancelBtnLabel="Cancel"
          isLoading={isDeletingTaskGroup}
        />
      )}

      {isDuplicateTaskGroupModal && (
        <Modal
          title="Duplication Confirmation"
          body={
            <p className="text-sm-400 subHeading-color">
              {`Are you sure you want to duplicate ${taskGroupData?.name || "this item"}${duplicateTaskGroupIncludeTasks ? " and its tasks" : ""}?`}
            </p>
          }
          handleCancel={() => {
            setIsDuplicateTaskGroupModal(false);
            setTaskGroupData(null);
          }}
          handleSave={handleTaskGroupDuplicateConfirm}
          saveBtnLabel="Confirm"
          cancelBtnLabel="Cancel"
          isLoading={isDuplicatingTaskGroup}
        />
      )}
    </>
  );
};

export default TaskTable;
